import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Send, 
  Users, 
  MessageCircle, 
  Loader2,
  Archive,
  ArchiveRestore,
  Trash2,
  Circle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const TeamChat = ({ teamId, teamName, onBack }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [roomDetails, setRoomDetails] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isArchived, setIsArchived] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get(`/chat/teams/${teamId}/messages?limit=100`);
      setMessages(response.data);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [teamId]);

  // Fetch room details
  const fetchRoomDetails = useCallback(async () => {
    try {
      const response = await api.get(`/chat/teams/${teamId}`);
      setRoomDetails(response.data);
      setIsArchived(response.data.is_archived);
    } catch (error) {
      console.error('Failed to fetch room details:', error);
    }
  }, [teamId]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/chat/ws/${teamId}?token=${token}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
      };

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'new_message':
            // Prevent duplicates (message might already be added from API response)
            setMessages(prev => {
              const exists = prev.some(m => m.id === data.message.id);
              if (exists) return prev;
              return [...prev, data.message];
            });
            setTimeout(scrollToBottom, 100);
            break;
          case 'user_joined':
            setOnlineUsers(prev => {
              if (!prev.find(u => u.user_id === data.user.user_id)) {
                return [...prev, data.user];
              }
              return prev;
            });
            break;
          case 'user_left':
            setOnlineUsers(prev => prev.filter(u => u.user_id !== data.user.user_id));
            break;
          case 'user_typing':
            setTypingUsers(prev => {
              if (!prev.find(u => u.user_id === data.user.user_id)) {
                return [...prev, data.user];
              }
              return prev;
            });
            // Remove typing indicator after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => prev.filter(u => u.user_id !== data.user.user_id));
            }, 3000);
            break;
          case 'chat_archived':
            setIsArchived(true);
            setMessages(prev => [...prev, data.message]);
            break;
          case 'chat_unarchived':
            setIsArchived(false);
            setMessages(prev => [...prev, data.message]);
            break;
          case 'chat_cleared':
            setMessages([data.message]);
            break;
          default:
            break;
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [teamId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMessages(), fetchRoomDetails()]);
      setLoading(false);
    };
    
    loadData();
    connectWebSocket();

    // Fetch online users periodically
    const onlineInterval = setInterval(async () => {
      try {
        const response = await api.get(`/chat/teams/${teamId}/online`);
        setOnlineUsers(response.data.online_users);
      } catch (error) {
        // Ignore
      }
    }, 10000);

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      clearInterval(onlineInterval);
    };
  }, [teamId, fetchMessages, fetchRoomDetails, connectWebSocket]);

  // Send message
  const handleSendMessage = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!newMessage.trim() || sending || isArchived) return;

    const messageContent = newMessage.trim();
    setSending(true);
    setNewMessage(''); // Clear input immediately for better UX
    
    try {
      const response = await api.post(`/chat/teams/${teamId}/messages`, {
        content: messageContent
      });
      
      // Always add message to state immediately for instant feedback
      // The WebSocket will handle deduplication if it also receives the message
      setMessages(prev => {
        // Check if message already exists (from WebSocket)
        const exists = prev.some(m => m.id === response.data.id);
        if (exists) return prev;
        return [...prev, response.data];
      });
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
      // Restore the message if it failed
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };
  
  // Focus input helper - uses requestAnimationFrame for reliable focus
  const focusInput = () => {
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
  };

  // Send typing indicator
  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Debounce typing indicator
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
      
      typingTimeoutRef.current = setTimeout(() => {
        // Stop sending typing indicator after 2 seconds of no typing
      }, 2000);
    }
  };

  // Archive/Unarchive chat
  const handleArchiveToggle = async () => {
    try {
      if (isArchived) {
        await api.post(`/chat/teams/${teamId}/unarchive`);
        toast.success('Chat unarchived');
      } else {
        await api.post(`/chat/teams/${teamId}/archive`);
        toast.success('Chat archived');
      }
    } catch (error) {
      toast.error('Failed to update archive status');
    }
  };

  // Clear chat history
  const handleClearChat = async () => {
    try {
      await api.delete(`/chat/teams/${teamId}/messages`);
      toast.success('Chat history cleared');
      setShowClearDialog(false);
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  // Format timestamp
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit' 
    });
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'staff': return 'bg-purple-100 text-purple-700';
      case 'coach': return 'bg-blue-100 text-blue-700';
      case 'parent': return 'bg-green-100 text-green-700';
      case 'system': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] lg:h-[calc(100vh-200px)] bg-white rounded-lg border shadow-sm" data-testid="team-chat">
      {/* Header */}
      <div className="flex items-center justify-between p-2.5 lg:p-4 border-b bg-slate-50">
        <div className="flex items-center gap-2 lg:gap-3 min-w-0">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="flex-shrink-0 h-8 w-8 p-0 lg:h-9 lg:w-auto lg:px-3">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="min-w-0">
            <h3 className="font-semibold text-sm lg:text-lg flex items-center gap-1.5 lg:gap-2 truncate">
              <MessageCircle className="h-4 w-4 lg:h-5 lg:w-5 text-blue-500 flex-shrink-0" />
              <span className="truncate">{teamName || roomDetails?.team_name}</span>
            </h3>
            <p className="text-xs lg:text-sm text-slate-500 truncate">
              {roomDetails?.tournament_name} {roomDetails?.age_category ? `• ${roomDetails.age_category.toUpperCase()}` : ''}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
          {/* Online indicator */}
          <div className="flex items-center gap-1 text-xs lg:text-sm text-slate-500">
            <Circle className="h-2 w-2 fill-green-500 text-green-500" />
            <span className="hidden lg:inline">{onlineUsers.length} online</span>
            <span className="lg:hidden">{onlineUsers.length}</span>
          </div>
          
          {/* Participants */}
          <Button variant="outline" size="sm" className="gap-1 h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3">
            <Users className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            {roomDetails?.participants?.length || 0}
          </Button>
          
          {/* Admin actions */}
          {user?.role === 'admin' && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleArchiveToggle}
                className={`h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3 ${isArchived ? 'text-green-600' : 'text-amber-600'}`}
              >
                {isArchived ? (
                  <><ArchiveRestore className="h-3.5 w-3.5 lg:h-4 lg:w-4 lg:mr-1" /><span className="hidden lg:inline">Unarchive</span></>
                ) : (
                  <><Archive className="h-3.5 w-3.5 lg:h-4 lg:w-4 lg:mr-1" /><span className="hidden lg:inline">Archive</span></>
                )}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowClearDialog(true)}
                className="text-red-600 h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
              >
                <Trash2 className="h-3.5 w-3.5 lg:h-4 lg:w-4 lg:mr-1" />
                <span className="hidden lg:inline">Clear</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Archived banner */}
      {isArchived && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-700 flex items-center gap-2">
          <Archive className="h-4 w-4" />
          This chat is archived. No new messages can be sent.
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-2.5 lg:p-4">
        <div className="space-y-3 lg:space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id}
                className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[85%] lg:max-w-[70%] rounded-lg p-2.5 lg:p-3 ${
                    msg.is_system_message 
                      ? 'bg-slate-100 text-slate-600 text-center w-full max-w-none text-xs lg:text-sm italic'
                      : msg.sender_id === user?.id
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-100'
                  }`}
                >
                  {!msg.is_system_message && msg.sender_id !== user?.id && (
                    <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                      <span className="font-medium text-xs lg:text-sm">{msg.sender_name}</span>
                      <Badge className={`text-[10px] lg:text-xs ${getRoleBadgeColor(msg.sender_role)}`}>
                        {msg.sender_role}
                      </Badge>
                    </div>
                  )}
                  <p className={`text-sm lg:text-base whitespace-pre-wrap ${msg.is_system_message ? '' : 'break-words'}`}>{msg.content}</p>
                  <p className={`text-[10px] lg:text-xs mt-1 ${
                    msg.sender_id === user?.id ? 'text-blue-100' : 'text-slate-400'
                  }`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <div className="px-4 py-1 text-sm text-slate-500 italic">
          {typingUsers.map(u => u.name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
        </div>
      )}

      {/* Input */}
      <div className="p-2.5 lg:p-4 border-t bg-slate-50" style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}>
        <div className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter inserts a newline.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                handleSendMessage(e);
                focusInput();
              }
            }}
            placeholder={isArchived ? "Chat is archived" : "Type a message... (Shift+Enter for a new line)"}
            disabled={isArchived || sending}
            className="flex-1 text-sm lg:text-base resize-none min-h-[40px] max-h-32 leading-tight"
            rows={1}
            maxLength={2000}
            autoFocus
            data-testid="chat-message-input"
          />
          <Button 
            type="button" 
            onClick={(e) => {
              handleSendMessage(e);
              focusInput();
            }}
            disabled={!newMessage.trim() || sending || isArchived}
            className="h-9 w-9 lg:h-10 lg:w-10 p-0"
            data-testid="chat-send-btn"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Clear chat dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Chat History</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all messages in this chat. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearChat} className="bg-red-500 hover:bg-red-600">
              Clear All Messages
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamChat;
